import * as React from "react";
import * as ToastPrimitives from "@radix-ui/react-toast";
import { cva, type VariantProps } from "class-variance-authority";
import { X, CheckCircle, AlertCircle, AlertTriangle, Info } from "lucide-react";

import { cn } from "@/lib/utils";

const ToastProvider = ToastPrimitives.Provider;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      "fixed top-4 right-0 z-[100] flex max-h-screen flex-col gap-3 p-4",
      "w-full sm:w-[400px] sm:max-w-[calc(100vw-2rem)]",
      "md:right-4 md:top-6 md:w-[420px]",
      "lg:right-6",
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitives.Viewport.displayName;

const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-start gap-3.5 overflow-hidden rounded-2xl border p-4 pr-10 shadow-xl shadow-black/8 backdrop-blur-sm transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-right-full data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
  {
    variants: {
      variant: {
        default:
          "border-slate-200/80 bg-white/95 text-slate-900 dark:border-slate-700/50 dark:bg-slate-900/95 dark:text-slate-100",
        success:
          "border-emerald-300/80 bg-gradient-to-br from-emerald-50/95 via-white/90 to-white/95 text-emerald-900 shadow-emerald-500/10 dark:border-emerald-800/50 dark:from-emerald-950/60 dark:via-slate-900/80 dark:to-slate-900/95 dark:text-emerald-100",
        error:
          "border-red-300/80 bg-gradient-to-br from-red-50/95 via-white/90 to-white/95 text-red-900 shadow-red-500/10 dark:border-red-800/50 dark:from-red-950/60 dark:via-slate-900/80 dark:to-slate-900/95 dark:text-red-100",
        warning:
          "border-amber-300/80 bg-gradient-to-br from-amber-50/95 via-white/90 to-white/95 text-amber-900 shadow-amber-500/10 dark:border-amber-800/50 dark:from-amber-950/60 dark:via-slate-900/80 dark:to-slate-900/95 dark:text-amber-100",
        info: "border-blue-300/80 bg-gradient-to-br from-blue-50/95 via-white/90 to-white/95 text-blue-900 shadow-blue-500/10 dark:border-blue-800/50 dark:from-blue-950/60 dark:via-slate-900/80 dark:to-slate-900/95 dark:text-blue-100",
        destructive:
          "border-red-300/80 bg-gradient-to-br from-red-50/95 via-white/90 to-white/95 text-red-900 shadow-red-500/10 dark:border-red-800/50 dark:from-red-950/60 dark:via-slate-900/80 dark:to-slate-900/95 dark:text-red-100",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const ToastIcon = ({ variant }: { variant?: string }) => {
  const iconClass = "h-5 w-5 shrink-0 drop-shadow-sm";

  switch (variant) {
    case "success":
      return (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40">
          <CheckCircle
            className={cn(iconClass, "text-emerald-600 dark:text-emerald-400")}
          />
        </div>
      );
    case "error":
    case "destructive":
      return (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/40">
          <AlertCircle
            className={cn(iconClass, "text-red-600 dark:text-red-400")}
          />
        </div>
      );
    case "warning":
      return (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/40">
          <AlertTriangle
            className={cn(iconClass, "text-amber-600 dark:text-amber-400")}
          />
        </div>
      );
    case "info":
      return (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/40">
          <Info className={cn(iconClass, "text-blue-600 dark:text-blue-400")} />
        </div>
      );
    default:
      return (
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800">
          <Info
            className={cn(iconClass, "text-slate-500 dark:text-slate-400")}
          />
        </div>
      );
  }
};

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
    VariantProps<typeof toastVariants>
>(({ className, variant, children, ...props }, ref) => {
  return (
    <ToastPrimitives.Root
      ref={ref}
      className={cn(toastVariants({ variant }), className)}
      {...props}
    >
      <ToastIcon variant={variant} />
      <div className="flex-1 min-w-0">{children}</div>
    </ToastPrimitives.Root>
  );
});
Toast.displayName = ToastPrimitives.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Action
    ref={ref}
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-muted/40 group-[.destructive]:hover:border-destructive/30 group-[.destructive]:hover:bg-destructive group-[.destructive]:hover:text-destructive-foreground group-[.destructive]:focus:ring-destructive",
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitives.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    className={cn(
      "absolute right-3 top-3 flex h-7 w-7 items-center justify-center rounded-full text-foreground/50 opacity-100 transition-all hover:text-foreground hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-offset-1 group-[.destructive]:text-red-300 group-[.destructive]:hover:text-red-50 group-[.destructive]:hover:bg-red-500/20 group-[.destructive]:focus:ring-red-400",
      className,
    )}
    toast-close=""
    {...props}
  >
    <X className="h-3.5 w-3.5" />
  </ToastPrimitives.Close>
));
ToastClose.displayName = ToastPrimitives.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn(
      "text-sm font-semibold leading-snug tracking-tight pr-2",
      className,
    )}
    {...props}
  />
));
ToastTitle.displayName = ToastPrimitives.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn("text-sm leading-relaxed opacity-80 mt-1", className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitives.Description.displayName;

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

type ToastActionElement = React.ReactElement<typeof ToastAction>;

export {
  type ToastProps,
  type ToastActionElement,
  ToastProvider,
  ToastViewport,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
};
